import React from 'react';
import cx from 'classnames';
import { Node } from 'slate';

export const PARAGRAFO_VAZIO_CLASS = 'mpf-paragrafo-vazio';

interface ParagraphElementProps {
  /** Nó do slate que este parágrafo desenha. */
  element?: Node;
  /** `view` na página publicada; `edit` na canvas do editor. */
  mode?: string;
  attributes?: { className?: string; [key: string]: unknown };
  [key: string]: unknown;
}

/**
 * Marca, só em modo de leitura, o parágrafo que não tem texto nenhum — o
 * caso típico é o parágrafo que a carga do conteúdo migrado criou a partir
 * do espaço em branco entre dois elementos do HTML original: um nó de texto
 * `"\n"` sozinho, que o `Leaf` do volto-slate desenha como `<br/>`.
 *
 * O portal atual não mostra esses parágrafos (um `<p>` só com espaço em
 * branco tem altura zero). Aqui, cada um virava uma linha em branco de 30 px
 * mais as margens do `prose`, e o conteúdo migrado ficava com o dobro da
 * altura (bug 11394, apontamento N13 do Victor). A classe deixa o CSS do
 * tema (`theme/styles/base/_editor-content.scss`) escondê-los.
 *
 * Só na leitura, de propósito: na canvas do editor o parágrafo precisa
 * continuar visível para o redator conseguir apagá-lo ou escrever nele.
 */
export const isParagrafoVazio = (element?: Node): boolean =>
  !!element && Node.string(element).trim() === '';

export const withParagrafoVazio = <P extends ParagraphElementProps>(
  Paragraph: React.ComponentType<P>,
): React.FC<P> => {
  const ParagrafoVazioAware: React.FC<P> = (props) => {
    if (props.mode !== 'view' || !isParagrafoVazio(props.element)) {
      return <Paragraph {...props} />;
    }
    const attributes = {
      ...(props.attributes || {}),
      className: cx(props.attributes?.className, PARAGRAFO_VAZIO_CLASS),
    };
    return <Paragraph {...props} attributes={attributes} />;
  };
  ParagrafoVazioAware.displayName = `withParagrafoVazio(${
    Paragraph.displayName || Paragraph.name || 'p'
  })`;
  return ParagrafoVazioAware;
};

export default withParagrafoVazio;
